<template>
    <!-- Informes del usuario property (/informes). Diseño de Mi cuenta
         (bkCuenta.css + cuentaMenu.css) + propertyPanel.css (prefijo pp-):
         lateral con título, cifras, contacto y los meses; a la derecha los
         archivos del mes (o los resultados de la búsqueda en todos los meses).
         Datos de siempre: GET apiUrls.informes y la descarga por
         apiUrls.informeDownload (blob con el token: el archivo es privado). -->
    <div class="pp-page">
        <div class="bk pp">
            <aside class="bk-side pp-side">
                <h1 class="bk-title">{{ $t('reports.title') }} <span class="bk-grad">{{ $t('reports.title-accent') }}</span></h1>
                <p class="bk-subtitle">{{ $t('reports.intro') }}</p>

                <div class="pp-stats" aria-live="polite">
                    <div class="pp-stat"><span class="pp-stat-n">{{ cargando ? '·' : informes.length }}</span><span class="pp-stat-k">{{ $t('property_panel.rep.stat_files') }}</span></div>
                    <div class="pp-stat"><span class="pp-stat-n">{{ cargando ? '·' : carpetas.length }}</span><span class="pp-stat-k">{{ $t('property_panel.rep.stat_folders') }}</span></div>
                    <div v-if="nuevos" class="pp-stat pp-stat--new"><span class="pp-stat-n">{{ nuevos }}</span><span class="pp-stat-k">{{ $t('property_panel.rep.stat_new') }}</span></div>
                </div>

                <div class="pp-contact"><PropertyContactButton /></div>

                <nav v-if="carpetas.length" class="bk-snav pp-months-nav" :aria-label="$t('reports.months')">
                    <template v-for="grupo in porAnio">
                        <p :key="'y-' + grupo.anio" class="bk-snav-title">{{ grupo.anio }}</p>
                        <ul :key="'l-' + grupo.anio" class="bk-snav-list">
                            <li v-for="carpeta in grupo.carpetas" :key="carpeta.periodo">
                                <button
                                    type="button"
                                    :class="['bk-snav-link', 'pp-month-link', { 'is-active': !buscando && carpeta.periodo === periodoActivo }]"
                                    :aria-current="!buscando && carpeta.periodo === periodoActivo ? 'true' : null"
                                    @click="elegirMes(carpeta.periodo)"
                                >
                                    <span aria-hidden="true" v-html="IC.folder"></span>
                                    <span class="pp-unit-txt">{{ nombreMes(carpeta.periodo) }}<small class="pp-chip-year"> {{ grupo.anio }}</small></span>
                                    <i v-if="tieneNuevos(carpeta)" class="pp-new-dot" :title="$t('reports.new')"></i>
                                    <span class="pp-n">{{ carpeta.archivos.length }}</span>
                                </button>
                            </li>
                        </ul>
                    </template>
                </nav>
            </aside>

            <section class="pp-main">
                <div v-if="cargando" class="pp-loading"><HyFLoader variant="inline" /></div>
                <div v-else-if="error" class="pp-empty pp-empty--error" role="alert"><span aria-hidden="true" v-html="IC.warn"></span><p>{{ error }}</p></div>
                <div v-else-if="!informes.length" class="pp-empty">
                    <span aria-hidden="true" v-html="IC.folder"></span>
                    <b>{{ $t('reports.empty') }}</b>
                    <p>{{ $t('reports.empty-hint') }}</p>
                </div>

                <template v-else>
                    <div class="pp-toolbar">
                        <label class="pp-search">
                            <span aria-hidden="true" v-html="IC.search"></span>
                            <span class="pp-sr">{{ $t('reports.search') }}</span>
                            <input v-model="busqueda" type="search" autocomplete="off" :placeholder="$t('reports.search')" @keydown.esc="busqueda = ''" />
                        </label>
                    </div>

                    <section class="pp-folder" :aria-labelledby="'pp-ft'">
                        <header class="pp-folder-head">
                            <span class="pp-folder-ic" aria-hidden="true" v-html="buscando ? IC.search : IC.folder"></span>
                            <div class="pp-folder-txt">
                                <h2 id="pp-ft">{{ buscando ? $t('reports.results') : (carpetaSel ? `${nombreMes(carpetaSel.periodo)} ${anioDe(carpetaSel.periodo)}` : '') }}</h2>
                                <p>{{ archivosVisibles.length }} {{ $tc('reports.report-count', archivosVisibles.length) }}<template v-if="buscando"> · «{{ busqueda.trim() }}»</template></p>
                            </div>
                            <button v-if="buscando" type="button" class="bk-btn bk-btn--ghost pp-clear" @click="busqueda = ''">{{ $t('property_panel.rep.clear') }}</button>
                        </header>

                        <p v-if="!archivosVisibles.length" class="pp-no-results">{{ $t('reports.no-results') }}</p>
                        <ul v-else class="pp-files">
                            <li v-for="inf in archivosVisibles" :key="inf.idinforme" class="pp-file">
                                <span :class="['pp-ext', 'pp-ext--' + tipoDe(inf)]" aria-hidden="true">{{ (inf.extension || '').toUpperCase() }}</span>
                                <div class="pp-file-txt">
                                    <b :title="inf.titulo || inf.nombre_original">{{ inf.titulo || inf.nombre_original }}<em v-if="esNuevo(inf)" class="pp-new">{{ $t('reports.new') }}</em></b>
                                    <small><template v-if="buscando">{{ nombreMes(periodoDe(inf)) }} {{ anioDe(periodoDe(inf)) }} · </template><template v-if="inf.titulo">{{ inf.nombre_original }} · </template>{{ formatoTamano(inf.tamano) }} · {{ formatoFecha(inf.created_at) }}</small>
                                </div>
                                <div class="pp-file-actions">
                                    <button v-if="sePuedeVer(inf)" type="button" class="bk-btn bk-btn--ghost pp-icon-btn" :disabled="abriendo === inf.idinforme" :title="$t('reports.view')" :aria-label="`${$t('reports.view')}: ${inf.nombre_original}`" @click="ver(inf, $event)">
                                        <span v-if="abriendo === inf.idinforme" class="bk-spinner"></span><span v-else aria-hidden="true" v-html="IC.eye"></span>
                                    </button>
                                    <button type="button" class="bk-btn bk-btn--primary pp-dl" :disabled="descargando === inf.idinforme" :aria-label="`${$t('reports.download')}: ${inf.nombre_original}`" @click="descargar(inf)">
                                        <span v-if="descargando === inf.idinforme" class="bk-spinner"></span><span v-else aria-hidden="true" v-html="IC.download"></span><span class="pp-dl-t">{{ $t('reports.download') }}</span>
                                    </button>
                                </div>
                            </li>
                        </ul>
                        <p v-if="errorDescarga" class="pp-error" role="alert">{{ errorDescarga }}</p>
                    </section>
                </template>
            </section>
        </div>

        <!-- Visor. El archivo está en S3 en privado y la API lo entrega solo con
             el token: se baja como blob y se muestra desde un object URL. -->
        <transition name="pp-modal">
            <div v-if="visor.informe" class="pp-viewer" @click.self="cerrarVisor" @keydown="teclaVisor">
                <div ref="visorCaja" class="pp-viewer-box" role="dialog" aria-modal="true" aria-labelledby="pp-v-title">
                    <header class="pp-viewer-head">
                        <span :class="['pp-ext', 'pp-ext--' + tipoDe(visor.informe)]" aria-hidden="true">{{ (visor.informe.extension || '').toUpperCase() }}</span>
                        <div class="pp-viewer-title">
                            <b id="pp-v-title">{{ visor.informe.titulo || visor.informe.nombre_original }}</b>
                            <small>{{ visor.informe.nombre_original }} · {{ formatoTamano(visor.informe.tamano) }}</small>
                        </div>
                        <div class="pp-viewer-actions">
                            <a v-if="visor.url" :href="visor.url" target="_blank" rel="noopener" class="bk-btn bk-btn--ghost pp-icon-btn" :title="$t('reports.open-tab')" :aria-label="$t('reports.open-tab')"><span aria-hidden="true" v-html="IC.ext"></span></a>
                            <button type="button" class="bk-btn bk-btn--primary pp-dl" @click="descargar(visor.informe)"><span aria-hidden="true" v-html="IC.download"></span><span class="pp-dl-t">{{ $t('reports.download') }}</span></button>
                            <button ref="visorCerrar" type="button" class="bk-btn bk-btn--ghost pp-icon-btn" :aria-label="$t('reports.close')" @click="cerrarVisor"><span aria-hidden="true" v-html="IC.x"></span></button>
                        </div>
                    </header>
                    <div class="pp-viewer-body">
                        <div v-if="visor.cargando" class="pp-loading"><HyFLoader variant="inline" /></div>
                        <p v-else-if="visor.error" class="pp-empty pp-empty--error">{{ visor.error }}</p>
                        <div v-else-if="visor.url && esImagen(visor.informe)" class="pp-img-wrap"><img :src="visor.url" :alt="visor.informe.nombre_original" class="pp-img" /></div>
                        <iframe v-else-if="visor.url" :src="visor.url" :title="visor.informe.nombre_original" class="pp-frame"></iframe>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from 'axios';
import apiUrls from '@/new-constants/apiUrls';
import HyFLoader from '@/components/Loader/HyFLoader.vue';
import PropertyContactButton from '@/components/Property/PropertyContactButton.vue';
import ICON from '@/components/Cuenta/iconos';

const P = d => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
// Íconos: texto fijo de este archivo, nunca datos del usuario.
const IC = {
    folder: P('<path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>'),
    eye: P('<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>'),
    ext: P('<path d="M14 4h6v6M20 4l-9 9M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"/>'),
    x: P('<path d="M18 6 6 18M6 6l12 12"/>'),
    warn: P('<path d="M12 3 2 20h20z"/><path d="M12 10v4M12 17h.01"/>'),
    search: ICON.search, download: ICON.download,
};
const MIMES = {
    pdf: 'application/pdf', txt: 'text/plain', doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
};
const IMAGENES = ['jpg', 'jpeg', 'png'];
/** Un archivo es "nuevo" durante esta cantidad de días después de subirse. */
const DIAS_NUEVO = 7;

/**
 * Sección "Informes" del usuario tipo property. Los archivos los carga un
 * admin desde /admin/informes, cada uno en la carpeta de un mes (`periodo`,
 * 'YYYY-MM'). Se navega por mes (agrupados por año en el lateral) y la
 * búsqueda filtra en todos los meses a la vez.
 */
export default {
    name: 'Informes',
    components: { HyFLoader, PropertyContactButton },
    data() {
        return {
            IC,
            informes: [],
            cargando: true,
            error: null,
            descargando: null,
            abriendo: null,
            errorDescarga: null,
            periodoSel: null,
            busqueda: '',
            visor: { informe: null, url: null, cargando: false, error: null },
        };
    },
    computed: {
        /** Carpetas de la más nueva a la más vieja. */
        carpetas() {
            const grupos = {};
            this.informes.forEach(inf => {
                const periodo = this.periodoDe(inf);
                (grupos[periodo] = grupos[periodo] || { periodo, archivos: [] }).archivos.push(inf);
            });
            return Object.keys(grupos).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0)).map(k => grupos[k]);
        },
        porAnio() {
            const anios = [], indice = {};
            this.carpetas.forEach(c => {
                const anio = this.anioDe(c.periodo);
                if (!(anio in indice)) { indice[anio] = anios.length; anios.push({ anio, carpetas: [] }); }
                anios[indice[anio]].carpetas.push(c);
            });
            return anios;
        },
        carpetaSel() { return this.carpetas.find(c => c.periodo === this.periodoSel) || this.carpetas[0] || null; },
        periodoActivo() { return this.carpetaSel ? this.carpetaSel.periodo : null; },
        buscando() { return this.busqueda.trim().length > 0; },
        nuevos() { return this.informes.filter(i => this.esNuevo(i)).length; },
        archivosVisibles() {
            if (this.buscando) {
                const q = this.busqueda.trim().toLowerCase();
                return this.informes.filter(i => `${i.titulo || ''} ${i.nombre_original || ''}`.toLowerCase().indexOf(q) !== -1);
            }
            return this.carpetaSel ? this.carpetaSel.archivos : [];
        },
    },
    methods: {
        async cargar() {
            this.cargando = true;
            this.error = null;
            try {
                const { data } = await axios.get(apiUrls.informes);
                this.informes = data.informes || [];
                this.periodoSel = this.carpetas.length ? this.carpetas[0].periodo : null;
            } catch (e) {
                console.error('[Informes] no se pudieron cargar los informes:', e);
                this.error = this.$t('reports.error');
            } finally {
                this.cargando = false;
            }
        },
        elegirMes(periodo) {
            this.periodoSel = periodo;
            this.busqueda = '';
            if (window.innerWidth < 1024) this.$nextTick(() => { const el = this.$el.querySelector('.pp-folder'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
        },
        periodoDe(inf) { return inf.periodo || String(inf.created_at || '').substring(0, 7); },
        anioDe(periodo) { return String(periodo || '').substring(0, 4); },
        /** '2026-09' → 'Septiembre' (en el idioma del usuario). */
        nombreMes(periodo) {
            const m = /^(\d{4})-(\d{2})$/.exec(periodo || '');
            if (!m) return periodo || '';
            const t = new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString(this.$i18n.locale, { month: 'long' });
            return t.charAt(0).toUpperCase() + t.slice(1);
        },
        esNuevo(inf) {
            if (!inf.created_at) return false;
            const f = new Date(String(inf.created_at).replace(' ', 'T'));
            return !isNaN(f.getTime()) && Date.now() - f.getTime() < DIAS_NUEVO * 86400000;
        },
        tieneNuevos(c) { return c.archivos.some(i => this.esNuevo(i)); },
        extensionDe(inf) { return ((inf && inf.extension) || '').toLowerCase(); },
        esImagen(inf) { return IMAGENES.indexOf(this.extensionDe(inf)) !== -1; },
        tipoDe(inf) {
            const ext = this.extensionDe(inf);
            if (IMAGENES.indexOf(ext) !== -1) return 'img';
            if (ext === 'doc' || ext === 'docx') return 'doc';
            return ext || 'otro';
        },
        /** PDF, TXT e imágenes se pueden mostrar; doc y docx no los abre ningún navegador. */
        sePuedeVer(inf) { return ['pdf', 'txt'].concat(IMAGENES).indexOf(this.extensionDe(inf)) !== -1; },
        async ver(inf, ev) {
            this._foco = (ev && ev.currentTarget) || document.activeElement;
            this.abriendo = inf.idinforme;
            this.errorDescarga = null;
            this.liberarUrl();
            this.visor = { informe: inf, url: null, cargando: true, error: null };
            this.$nextTick(() => this.$refs.visorCerrar && this.$refs.visorCerrar.focus());
            try {
                const response = await axios.get(apiUrls.informeDownload(inf.idinforme), { responseType: 'blob' });
                // El type importa: sin él el navegador ofrece descargarlo en vez de mostrarlo.
                const blob = new Blob([response.data], { type: inf.mime || this.mimePorExtension(inf.extension) });
                if (this.visor.informe === inf) this.visor.url = window.URL.createObjectURL(blob);
            } catch (e) {
                console.error('[Informes] no se pudo abrir el informe:', e);
                this.visor.error = this.$t('reports.download-error');
            } finally {
                this.visor.cargando = false;
                this.abriendo = null;
            }
        },
        cerrarVisor() {
            this.liberarUrl();
            this.visor = { informe: null, url: null, cargando: false, error: null };
            const el = this._foco;
            this.$nextTick(() => { if (el && document.contains(el)) el.focus(); });
        },
        // Esc cierra; el Tab queda dentro del visor.
        teclaVisor(e) {
            if (e.key === 'Escape') { e.preventDefault(); this.cerrarVisor(); return; }
            if (e.key !== 'Tab' || !this.$refs.visorCaja) return;
            const els = Array.from(this.$refs.visorCaja.querySelectorAll('a[href], button:not(:disabled), iframe'));
            if (!els.length) return;
            const first = els[0], last = els[els.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        },
        onKeydown(e) { if (e.key === 'Escape' && this.visor.informe) this.cerrarVisor(); },
        liberarUrl() {
            if (this.visor.url) { window.URL.revokeObjectURL(this.visor.url); this.visor.url = null; }
        },
        mimePorExtension(ext) { return MIMES[(ext || '').toLowerCase()] || 'application/octet-stream'; },
        async descargar(inf) {
            this.descargando = inf.idinforme;
            this.errorDescarga = null;
            try {
                const response = await axios.get(apiUrls.informeDownload(inf.idinforme), { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', inf.nombre_original);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (e) {
                console.error('[Informes] falló la descarga:', e);
                this.errorDescarga = this.$t('reports.download-error');
            } finally {
                this.descargando = null;
            }
        },
        formatoFecha(v) {
            if (!v) return '—';
            const f = new Date(String(v).replace(' ', 'T'));
            return isNaN(f.getTime()) ? String(v).substring(0, 10) : f.toLocaleDateString(this.$i18n.locale, { day: '2-digit', month: 'short', year: 'numeric' });
        },
        formatoTamano(bytes) {
            const n = Number(bytes) || 0;
            if (n < 1024) return `${n} B`;
            if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
            return `${(n / 1048576).toFixed(1)} MB`;
        },
    },
    created() { this.cargar(); },
    mounted() { window.addEventListener('keydown', this.onKeydown); },
    beforeDestroy() {
        window.removeEventListener('keydown', this.onKeydown);
        // El object URL vive hasta que se lo revoque: si no, el archivo queda en memoria.
        this.liberarUrl();
    },
};
</script>

<style src="../../components/BankInformation/bkCuenta.css"></style>
<style src="../../components/Cuenta/cuentaMenu.css"></style>
<style src="../../components/Property/propertyPanel.css"></style>
